import type { ConfigProps } from '../../configTypes';
import transformImage from '../transform/transformImage';
import fetchDefaultContributor from '../queries/fetchDefaultContributor';
import { getContributorPath } from './contributorPaths';

type ParsedContributorData = {
  name: string;
  excerpt: string;
  slug: string;
  image: string | null;
  career?: string;
};

function estimateReadTime(content: string): number {
  const textContent = content.replace(/<[^>]*>/g, '');
  const wordCount = textContent.split(/\s+/).length;
  return Math.ceil(wordCount / 200);
}

export default async function getByline(
  post: {
    content?: string;
    contributors?: unknown;
    customDateType?: string;
    date?: string;
    additionalBylineDescription?: string;
  },
  enrichedSiteSettings: ConfigProps,
) {
  const contributors = post.contributors as
    | { types?: Array<{ key: string; value?: unknown[] }> }
    | undefined;
  const getContributorImage = (contributor: { featuredImage?: string }) => {
    const image =
      contributor.featuredImage ||
      (enrichedSiteSettings.hero as { byline?: { image?: string } })?.byline
        ?.image;
    return image ? transformImage(image, { type: 'thumb' }) : null;
  };

  const parseContributorData = (c: {
    title?: string;
    content?: string;
    slug?: string;
    featuredImage?: string;
    career?: string;
  }): ParsedContributorData => ({
    name: c.title ?? '',
    excerpt: c.content ?? '',
    slug: c.slug ?? '',
    image: getContributorImage(c),
    career: c.career,
  });

  type WriterType = { key: string; value?: unknown[] };
  let writer = contributors?.types?.find((t: WriterType) => t.key === 'writer');
  if (!writer?.value?.length) {
    const defaultContributor =
      await fetchDefaultContributor(enrichedSiteSettings);
    writer = {
      key: 'writer',
      value: defaultContributor != null ? [defaultContributor] : [],
    } as WriterType;
  }
  const writtenBy = (writer?.value ?? [])
    .filter(Boolean)
    .map((c) =>
      parseContributorData(c as Parameters<typeof parseContributorData>[0]),
    );
  const editor = contributors?.types?.find(
    (t: WriterType) => t.key === 'editor',
  );
  const editedBy = editor
    ? (editor.value ?? []).map((c) =>
        parseContributorData(c as Parameters<typeof parseContributorData>[0]),
      )
    : [];
  const reviewer = contributors?.types?.find(
    (t: WriterType) => t.key === 'reviewer',
  );
  const reviewedBy = reviewer
    ? (reviewer.value ?? []).map((c) =>
        parseContributorData(c as Parameters<typeof parseContributorData>[0]),
      )
    : [];
  const readTime = post.content ? estimateReadTime(post.content) : 0;
  const isUpdated = post.customDateType !== 'show_published_date';
  const date = post.date
    ? new Date(post.date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        timeZone: 'UTC',
      })
    : null;

  return {
    writtenBy,
    editedBy,
    reviewedBy,
    readTime,
    date,
    domain: enrichedSiteSettings.domainLabelWithExtension,
    isUpdated,
    contributorPath: getContributorPath(enrichedSiteSettings),
    additionalBylineDescription: post.additionalBylineDescription || '',
  };
}
